import { QueryResult } from 'react-query';
import { callApi, Controller, useApi, UseApiOptions } from './useApi';
import {
    addLieferantenrechnungAraPosition,
    defaultLieferantenrechnungAraPosition,
} from './lieferantenrechnungAraPosition';
import { getArtikelDetailKalkulationen } from './artikelDetailKalkulation';
import { getArtikelDetail } from './artikelDetail';

export interface LieferantenrechnungPosition {
    rNr: string;
    artikelGruppeNr: number;
    artikelNr: string;
    pos: number;
    posText: string;
    posEinh: string;
    posMenge: number;
    posEkPreis: number;
    posEPreis: number;
    posNetto: number;
    posMwst: number;
    posBrutto: number;
    posBem: string;
    lkz: number;
    pd: number;
    pz: number;
    lNr: number;

    updDat?: string;
    updUhr?: number;
    updUser?: string;
}

export interface LieferantenrechnungPositionFilter {
    rNrList?: string[];
    kNr?: number;
    rNr?: string;
    pos?: number;
}

export const defaultLieferantenrechnungPosition: LieferantenrechnungPosition = {
    rNr: '',
    artikelGruppeNr: 0,
    artikelNr: '',
    pos: -1, // -1 = new
    posText: '',
    posEinh: '',
    posMenge: 0,
    posEkPreis: 0,
    posEPreis: 0,
    posNetto: 0,
    posMwst: 20,
    posBrutto: 0,
    posBem: '',
    lkz: 0,
    pd: 0,
    pz: 0,
    lNr: 0,
};

export function useLieferantenrechnungPositionList(
    filter: LieferantenrechnungPositionFilter,
    apiOptions?: UseApiOptions<LieferantenrechnungPosition[]>,
): QueryResult<LieferantenrechnungPosition[]> {
    return useApi<LieferantenrechnungPosition[]>(
        Controller.lieferantenrechnungPosition + '/list',
        { filter },
        apiOptions,
    );
}

interface AddLieferantenRechnungPositionResult {
    lieferantenrechnungPosition: LieferantenrechnungPosition;
}

export async function addLieferantenrechnungPosition(
    lieferantenrechnungPosition: LieferantenrechnungPosition,
): Promise<AddLieferantenRechnungPositionResult> {
    const ret = await callApi<AddLieferantenRechnungPositionResult>(Controller.lieferantenrechnungPosition + '/add', {
        lieferantenrechnungPosition,
    });

    const artikelNrList = ['KERNE', 'TRKARTON', 'TRFOLIEN', 'TRHOLZ', 'TREISEN'];

    // eslint-disable-next-line no-restricted-syntax
    for (const artikelNr of artikelNrList) {
        // eslint-disable-next-line no-await-in-loop
        const artikelDetail = await getArtikelDetail({
            artikelGruppeNr: 97,
            artikelNr,
        });

        // eslint-disable-next-line no-await-in-loop
        const kalkulationen = await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr,
        });

        const kalkulation = kalkulationen.find((k) => k.eh === 'K');

        // eslint-disable-next-line no-await-in-loop
        await addLieferantenrechnungAraPosition({
            ...defaultLieferantenrechnungAraPosition,
            rNr: ret.lieferantenrechnungPosition.rNr,
            rPos: ret.lieferantenrechnungPosition.pos,
            artikelGruppeNr: 97,
            artikelNr,
            posText: artikelDetail.artikel?.text || '?',
            posEinh: 'K',
            posEkPreis: kalkulation?.ek1 || 0,
            posEPreis: kalkulation?.ek1 || 0,
            posMwst: lieferantenrechnungPosition.posMwst,
        });
    }

    /*
    const kerneKalkulation = (
        await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr: 'KERNE',
        })
    ).find((k) => k.eh === 'K');

    await addLieferantenrechnungAraPosition({
        ...defaultLieferantenrechnungAraPosition,
        rNr: ret.lieferantenrechnungPosition.rNr,
        rPos: ret.lieferantenrechnungPosition.pos,
        artikelGruppeNr: 97,
        artikelNr: 'KERNE',
        posText: 'Entpflichtung Kerne',
        posEinh: 'K',
        posEkPreis: kerneKalkulation?.ek1 || 0,
        posEPreis: kerneKalkulation?.ek1 || 0,
        posMwst: lieferantenrechnungPosition.posMwst,
    });

    const trkartonKalkulation = (
        await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr: 'TRKARTON',
        })
    ).find((k) => k.eh === 'K');

    await addLieferantenrechnungAraPosition({
        ...defaultLieferantenrechnungAraPosition,
        rNr: ret.lieferantenrechnungPosition.rNr,
        rPos: ret.lieferantenrechnungPosition.pos,
        artikelGruppeNr: 97,
        artikelNr: 'TRKARTON',
        posText: 'Transportverpackung Karton Entpflichtung',
        posEinh: 'K',
        posEkPreis: trkartonKalkulation?.ek1 || 0,
        posEPreis: trkartonKalkulation?.ek1 || 0,
        posMwst: lieferantenrechnungPosition.posMwst,
    });

    const trfolienKalkulation = (
        await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr: 'TRFOLIEN',
        })
    ).find((k) => k.eh === 'K');

    await addLieferantenrechnungAraPosition({
        ...defaultLieferantenrechnungAraPosition,
        rNr: ret.lieferantenrechnungPosition.rNr,
        rPos: ret.lieferantenrechnungPosition.pos,
        artikelGruppeNr: 97,
        artikelNr: 'TRFOLIEN',
        posText: 'Transportverpackung Folien Entpflichtung',
        posEinh: 'K',
        posEkPreis: trfolienKalkulation?.ek1 || 0,
        posEPreis: trfolienKalkulation?.ek1 || 0,
        posMwst: lieferantenrechnungPosition.posMwst,
    });

    const trholzKalkulation = (
        await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr: 'TRHOLZ',
        })
    ).find((k) => k.eh === 'K');

    await addLieferantenrechnungAraPosition({
        ...defaultLieferantenrechnungAraPosition,
        rNr: ret.lieferantenrechnungPosition.rNr,
        rPos: ret.lieferantenrechnungPosition.pos,
        artikelGruppeNr: 97,
        artikelNr: 'TRHOLZ',
        posText: 'Transportverpackung Holz gewerblich',
        posEinh: 'K',
        posEkPreis: trholzKalkulation?.ek1 || 0,
        posEPreis: trholzKalkulation?.ek1 || 0,
        posMwst: lieferantenrechnungPosition.posMwst,
    });

    const treisenKalkulation = (
        await getArtikelDetailKalkulationen({
            artikelGruppeNr: 97,
            artikelNr: 'TREISEN',
        })
    ).find((k) => k.eh === 'K');

    await addLieferantenrechnungAraPosition({
        ...defaultLieferantenrechnungAraPosition,
        rNr: ret.lieferantenrechnungPosition.rNr,
        rPos: ret.lieferantenrechnungPosition.pos,
        artikelGruppeNr: 97,
        artikelNr: 'TREISEN',
        posText: 'Transportverpackung Eisenmetalle gewerblich',
        posEinh: 'K',
        posEkPreis: treisenKalkulation?.ek1 || 0,
        posEPreis: treisenKalkulation?.ek1 || 0,
        posMwst: lieferantenrechnungPosition.posMwst,
    });
     */

    return ret;
}

export async function updateLieferantenrechnungPosition(
    lieferantenrechnungPosition: LieferantenrechnungPosition,
): Promise<void> {
    return callApi<void>(Controller.lieferantenrechnungPosition + '/update', { lieferantenrechnungPosition });
}

export async function deleteLieferantenrechnungPosition(
    lieferantenrechnungPosition: LieferantenrechnungPosition,
): Promise<void> {
    return callApi<void>(Controller.lieferantenrechnungPosition + '/delete', { lieferantenrechnungPosition });
}

export async function moveUpLieferantenrechnungPosition(rNr: string, pos: number): Promise<void> {
    return callApi<void>(Controller.lieferantenrechnungPosition + '/moveup', {
        lieferantenrechnungPosition: {
            rNr,
            pos,
        },
    });
}
